/**
 * HR Replacement Grants page — /hr/replacement-grants
 *
 * Three-tab overview of replacement grants across all employees with
 * stat cards (active / used YTD / expired YTD), inline edit, and a
 * cancel action gated on consumed == 0. The grant-creation form lives
 * inside a modal opened from the "+ Grant replacement leave" button.
 *
 * Gated behind feature flag `replacementGrantsAdminUi`.
 */
import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { differenceInCalendarDays, parseISO } from 'date-fns'
import { fmtDate } from '../lib/format'
import { ApiError } from '../api/client'
import {
  cancelReplacementGrant,
  listAllReplacementGrants,
  updateReplacementGrant,
  type ReplacementGrant,
  type ReplacementGrantStatus,
} from '../api/replacement-grants'
import { Avatar } from '../components/primitives/Avatar'
import { Button } from '../components/primitives/Button'
import { Card } from '../components/primitives/Card'
import { ConfirmationDialog } from '../components/primitives/ConfirmationDialog'
import { DateField } from '../components/primitives/DateField'
import { FadeIn } from '../components/primitives/FadeIn'
import { Field } from '../components/primitives/Field'
import { Icon } from '../components/primitives/Icon'
import { Modal } from '../components/primitives/Modal'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { useToast } from '../components/primitives/Toast'
import { ReplacementGrantForm } from '../components/hr/ReplacementGrantForm'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'

type TabKey = ReplacementGrantStatus // 'active' | 'expired' | 'fully_consumed'

const TAB_LABEL: Record<TabKey, string> = {
  active: 'Active',
  fully_consumed: 'Used',
  expired: 'Expired',
}

// Full-width inside the Leaves hub — the hub's `.page` container already
// provides the max-width and responsive side gutters, so this surface must not
// add its own narrower max-width (which made it read smaller than the other
// tabs). Just keep a bottom gutter.
const pageStyle: CSSProperties = {
  paddingBottom: 40,
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (${err.status})`
  if (err instanceof Error) return err.message
  return 'Unexpected error'
}

// ── Status filter (dropdown) ──────────────────────────────────────────────────

type FilterKey = 'all' | 'active' | 'expired'

interface GrantsFilterProps {
  value: FilterKey
  counts: { all: number; active: number; expired: number }
  onChange: (key: FilterKey) => void
}

function GrantsFilter({ value, counts, onChange }: GrantsFilterProps): JSX.Element {
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'stretch' }}>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          paddingLeft: 12,
          paddingRight: 6,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-3)',
          border: '1px solid var(--border)',
          borderRight: 'none',
          borderRadius: '8px 0 0 8px',
          background: 'var(--surface-2)',
        }}
      >
        Status
      </span>
      <select
        aria-label="Filter grants by status"
        data-testid="rg-filter"
        value={value}
        onChange={(e) => onChange(e.target.value as FilterKey)}
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          minHeight: 36,
          padding: '6px 34px 6px 12px',
          borderRadius: '0 8px 8px 0',
          border: '1px solid var(--border)',
          background: 'var(--surface)',
          color: 'var(--text)',
          fontFamily: 'var(--font-sans)',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <option value="all">All ({counts.all})</option>
        <option value="active">Active ({counts.active})</option>
        <option value="expired">Expired ({counts.expired})</option>
      </select>
      <Icon
        name="expand_more"
        size={18}
        aria-hidden
        style={{
          position: 'absolute',
          right: 9,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: 'var(--text-2)',
        }}
      />
    </div>
  )
}

// ── Status pill ───────────────────────────────────────────────────────────────
// Shared `.badge.dot.b-*` vocabulary (same as My Leaves / the Applications tab).

const STATUS_PILL: Record<TabKey, { cls: string; label: string }> = {
  active: { cls: 'badge dot b-success', label: 'active' },
  fully_consumed: { cls: 'badge dot b-gray', label: 'used' },
  expired: { cls: 'badge dot b-danger', label: 'expired' },
}

function StatusPill({ status }: { status: TabKey }): JSX.Element {
  const s = STATUS_PILL[status]
  return <span className={s.cls}>{s.label}</span>
}

// ── Row icon buttons ──────────────────────────────────────────────────────────

interface RowActionProps {
  label: string
  icon: 'edit' | 'delete'
  tone?: 'neutral' | 'danger'
  onClick: () => void
  disabled?: boolean
  testId?: string
}

function RowAction({
  label,
  icon,
  tone = 'neutral',
  onClick,
  disabled = false,
  testId,
}: RowActionProps): JSX.Element {
  const color =
    tone === 'danger' ? 'var(--danger)' : 'var(--text-2)'
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        width: 32,
        height: 32,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: '1px solid var(--border)',
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        color,
        padding: 0,
        transition: 'background 80ms ease, color 80ms ease, border-color 80ms ease',
      }}
      onMouseEnter={(e) => {
        if (disabled) return
        const el = e.currentTarget as HTMLButtonElement
        el.style.background =
          tone === 'danger' ? 'var(--danger-bg, #fef2f2)' : 'var(--surface-2)'
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLButtonElement
        el.style.background = 'transparent'
      }}
    >
      <Icon name={icon} size={16} />
    </button>
  )
}

// ── Edit modal ────────────────────────────────────────────────────────────────

interface EditModalProps {
  grant: ReplacementGrant
  onClose: () => void
  onSaved: () => void
}

function EditGrantModal({ grant, onClose, onSaved }: EditModalProps): JSX.Element {
  const { notify } = useToast()
  const queryClient = useQueryClient()
  const [notes, setNotes] = useState<string>(grant.notes ?? '')
  const [days, setDays] = useState<string>(grant.quantity)
  const [expiresAt, setExpiresAt] = useState<string>(grant.expiresAt)
  const [workedDatesText, setWorkedDatesText] = useState<string>(
    grant.workedDates.join(', '),
  )

  const consumed = Number(grant.consumed)

  const mutation = useMutation({
    mutationFn: () => {
      const parsedDates = workedDatesText
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean)
      const body: Parameters<typeof updateReplacementGrant>[1] = {
        workedDates: parsedDates,
        quantity: days,
        expiresAt,
        notes: notes.trim() === '' ? null : notes.trim(),
      }
      return updateReplacementGrant(grant.id, body)
    },
    onSuccess: () => {
      notify('Grant updated', 'success')
      void queryClient.invalidateQueries({ queryKey: ['replacement-grants'] })
      onSaved()
    },
  })

  const minQty = consumed > 0 ? consumed : 0.5

  return (
    <Modal open onClose={onClose} title="Edit replacement grant">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          mutation.mutate()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}
      >
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          For <strong>{grant.employeeName ?? grant.employeeId.slice(0, 8)}</strong>
          {consumed > 0 ? ` · ${consumed} day(s) already used` : ''}
        </div>

        <Field label="Earned for (notes)" htmlFor="rg-edit-notes">
          <textarea
            id="rg-edit-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 13,
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
              minHeight: 72,
              resize: 'vertical',
              boxSizing: 'border-box',
              fontFamily: 'var(--font-sans)',
            }}
          />
        </Field>

        <Field
          label="Worked dates (comma-separated YYYY-MM-DD)"
          htmlFor="rg-edit-dates"
        >
          <input
            id="rg-edit-dates"
            type="text"
            value={workedDatesText}
            onChange={(e) => setWorkedDatesText(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 13,
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
              minHeight: 44,
              boxSizing: 'border-box',
              fontFamily: 'var(--font-sans)',
            }}
          />
        </Field>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ width: 140 }}>
            <Field label="Days" htmlFor="rg-edit-days">
              <input
                id="rg-edit-days"
                type="number"
                step={0.5}
                min={minQty}
                max={5}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 13,
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  minHeight: 44,
                  boxSizing: 'border-box',
                  fontFamily: 'var(--font-sans)',
                }}
              />
            </Field>
            {consumed > 0 ? (
              <p
                style={{ fontSize: 11, color: 'var(--text-3)', margin: '4px 0 0' }}
              >
                Cannot go below {consumed} (consumed)
              </p>
            ) : null}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Field label="Expires on" htmlFor="rg-edit-expires">
              <DateField
                value={expiresAt || null}
                onChange={(iso) => setExpiresAt(iso ?? '')}
              />
            </Field>
          </div>
        </div>

        {mutation.isError ? (
          <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, margin: 0 }}>
            {errorMessage(mutation.error)}
          </p>
        ) : null}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            paddingTop: 4,
          }}
        >
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// ── Cancel grant dialog ──────────────────────────────────────────────────────

interface CancelDialogProps {
  grant: ReplacementGrant
  onClose: () => void
  onCancelled: () => void
}

function CancelGrantDialog({
  grant,
  onClose,
  onCancelled,
}: CancelDialogProps): JSX.Element {
  const { notify } = useToast()
  const queryClient = useQueryClient()
  const consumed = Number(grant.consumed)

  const mutation = useMutation({
    mutationFn: () => cancelReplacementGrant(grant.id),
    onSuccess: () => {
      notify('Grant cancelled', 'success')
      void queryClient.invalidateQueries({ queryKey: ['replacement-grants'] })
      onCancelled()
    },
  })

  const blocked = consumed > 0

  return (
    <ConfirmationDialog
      open
      onClose={onClose}
      title="Cancel replacement grant?"
      subtitle={
        blocked
          ? 'This grant has been used and cannot be cancelled.'
          : `This permanently removes the grant for ${grant.employeeName ?? 'this employee'}.`
      }
      confirmLabel={mutation.isPending ? 'Cancelling…' : 'Cancel grant'}
      cancelLabel="Keep"
      variant="danger"
      confirmDisabled={blocked || mutation.isPending}
      loading={mutation.isPending}
      onConfirm={() => mutation.mutate()}
      data-testid="rg-cancel-dialog"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
        <div>
          <strong>{grant.quantity}</strong> day(s), earned for{' '}
          <em>{grant.notes ?? 'no description'}</em>
        </div>
        <div style={{ color: 'var(--text-3)' }}>
          Granted {fmtDate(grant.grantedAt)} · expires {fmtDate(grant.expiresAt)}
        </div>
        {blocked ? (
          <div
            role="alert"
            style={{
              marginTop: 8,
              padding: 10,
              borderRadius: 6,
              background: 'var(--danger-bg, #fef2f2)',
              color: 'var(--danger)',
              fontSize: 12,
              lineHeight: 1.4,
            }}
          >
            {consumed} day(s) already used. Cancel the redeemed leave first to
            restore this grant.
          </div>
        ) : null}
        {mutation.isError ? (
          <p
            role="alert"
            style={{ color: 'var(--danger)', fontSize: 12, margin: '4px 0 0' }}
          >
            {errorMessage(mutation.error)}
          </p>
        ) : null}
      </div>
    </ConfirmationDialog>
  )
}

// ── Grants table ──────────────────────────────────────────────────────────────

interface GrantsTableProps {
  rows: ReplacementGrant[]
  onEdit: (g: ReplacementGrant) => void
  onCancel: (g: ReplacementGrant) => void
}

function GrantsTable({ rows, onEdit, onCancel }: GrantsTableProps): JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="placeholder" style={{ border: 'none', borderRadius: 0 }}>
        No grants match this filter.
      </div>
    )
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      {/* Fixed layout with proportional widths (sum 100%) so the table fits one
          view without horizontal scroll; the Employee cell truncates. */}
      <table
        className="tbl"
        aria-label="Replacement grants"
        style={{ tableLayout: 'fixed', width: '100%' }}
      >
        <thead>
          <tr>
            <th style={{ width: '24%' }}>Employee</th>
            <th style={{ width: '20%' }}>Earned for</th>
            <th style={{ width: '14%' }}>Granted</th>
            <th style={{ width: '8%' }}>Days</th>
            <th style={{ width: '14%' }}>Expires</th>
            <th style={{ width: '12%' }}>Status</th>
            <th style={{ width: '8%' }} aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((g) => {
            const daysLeft = differenceInCalendarDays(parseISO(g.expiresAt), today)
            const expiringSoon =
              g.status === 'active' && daysLeft >= 0 && daysLeft <= 30
            const cancelDisabled = Number(g.consumed) > 0
            return (
              <tr key={g.id} data-testid={`rg-row-${g.id}`}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <Avatar
                      name={g.employeeName ?? '??'}
                      size={28}
                    />
                    <span
                      title={g.employeeName ?? g.employeeId.slice(0, 8)}
                      style={{
                        fontWeight: 500,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {g.employeeName ?? g.employeeId.slice(0, 8)}
                    </span>
                  </div>
                </td>
                <td style={{ color: 'var(--text-2)' }}>
                  {g.notes ?? '—'}
                </td>
                <td style={{ color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                  {fmtDate(g.grantedAt)}
                </td>
                <td style={{ fontVariantNumeric: 'tabular-nums' }}>{g.quantity}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ marginRight: 8 }}>{fmtDate(g.expiresAt)}</span>
                  {expiringSoon ? (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: '#fef3c7',
                        color: '#92400e',
                      }}
                    >
                      {daysLeft}d left
                    </span>
                  ) : null}
                </td>
                <td>
                  <StatusPill status={g.status} />
                </td>
                <td style={{ width: 96 }}>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <RowAction
                      label="Edit grant"
                      icon="edit"
                      onClick={() => onEdit(g)}
                      testId={`rg-edit-${g.id}`}
                    />
                    <RowAction
                      label={
                        cancelDisabled
                          ? 'Cannot cancel — grant has been used'
                          : 'Cancel grant'
                      }
                      icon="delete"
                      tone="danger"
                      onClick={() => onCancel(g)}
                      disabled={cancelDisabled}
                      testId={`rg-cancel-${g.id}`}
                    />
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function HrReplacementGrantsPage(): JSX.Element {
  const [filter, setFilter] = useState<FilterKey>('all')
  const [createOpen, setCreateOpen] = useState<boolean>(false)
  const [editing, setEditing] = useState<ReplacementGrant | null>(null)
  const [cancelling, setCancelling] = useState<ReplacementGrant | null>(null)

  const grantsQuery = useQuery({
    queryKey: ['replacement-grants', 'all'],
    queryFn: listAllReplacementGrants,
    refetchOnWindowFocus: false,
  })

  const grants = grantsQuery.data ?? []

  const counts = useMemo(() => {
    let active = 0
    let expired = 0
    for (const g of grants) {
      if (g.status === 'active') active += 1
      else if (g.status === 'expired') expired += 1
    }
    // Fully-consumed ("used") grants aren't tracked here, so "All" = the ones
    // HR cares about: active + expired.
    return { all: active + expired, active, expired }
  }, [grants])

  const rows = useMemo(
    () =>
      grants.filter((g) =>
        filter === 'all' ? g.status === 'active' || g.status === 'expired' : g.status === filter,
      ),
    [grants, filter],
  )

  return (
    <HrAccessGuard>
      <FadeIn>
        <div style={pageStyle}>
          {/* Status dropdown sits just left of the Grant CTA, both right-aligned. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 16,
            }}
          >
            <GrantsFilter value={filter} counts={counts} onChange={setFilter} />
            <Button
              variant="primary"
              onClick={() => setCreateOpen(true)}
              iconLeft={<Icon name="add" size={16} />}
              data-testid="rg-open-create"
            >
              Grant replacement leave
            </Button>
          </div>

          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {grantsQuery.isPending ? (
              <div style={{ padding: 24 }}>
                <ThinkingDots label="Loading grants" />
              </div>
            ) : grantsQuery.isError ? (
              <div style={{ padding: 24, color: 'var(--danger)', fontSize: 13 }}>
                Could not load grants: {errorMessage(grantsQuery.error)}
              </div>
            ) : (
              <GrantsTable
                rows={rows}
                onEdit={(g) => setEditing(g)}
                onCancel={(g) => setCancelling(g)}
              />
            )}
          </Card>
        </div>
      </FadeIn>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Grant replacement leave"
        maxWidth={720}
      >
        <ReplacementGrantForm
          onCreated={() => {
            setCreateOpen(false)
          }}
        />
      </Modal>

      {editing ? (
        <EditGrantModal
          grant={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      ) : null}

      {cancelling ? (
        <CancelGrantDialog
          grant={cancelling}
          onClose={() => setCancelling(null)}
          onCancelled={() => setCancelling(null)}
        />
      ) : null}
    </HrAccessGuard>
  )
}
